import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from 'react-bootstrap';
import { AlertTriangle, CheckCircle, Clock, Layers, Plus, Save, Trash2 } from 'lucide-react';
import { AdminApiDomain, getAdminApiUrl } from '@/config/ApiRouting';
import { SiteConfigClient } from '@/infrastructure/SiteConfigClient';
import { BUNDLE_RECIPES_CONFIG_KEY, type BundleRecipesConfig } from '@/models/BundleRecipesConfig';
import { validateBundleRecipesConfig } from '@/pages/oms/bundle-recipes/BundleRecipesConfigValidator';

interface ComponentDraft {
  sku: string;
  quantity: string;
}

interface RecipeDraft {
  bundleSku: string;
  components: ComponentDraft[];
}

const EMPTY_COMPONENT: ComponentDraft = { sku: '', quantity: '1' };

const toDrafts = (config: BundleRecipesConfig): RecipeDraft[] =>
  Object.entries(config.recipes ?? {}).map(([bundleSku, components]) => ({
    bundleSku,
    components: components.map(({ sku, quantity }) => ({ sku, quantity: String(quantity) })),
  }));

const toConfig = (drafts: RecipeDraft[]): BundleRecipesConfig => ({
  recipes: Object.fromEntries(
    drafts.map(({ bundleSku, components }) => [
      bundleSku.trim(),
      components.map(({ sku, quantity }) => ({
        sku: sku.trim(),
        quantity: Number(quantity),
      })),
    ]),
  ),
});

const findDuplicateBundleSku = (drafts: RecipeDraft[]): string | undefined => {
  const bundleSkus = drafts.map(({ bundleSku }) => bundleSku.trim()).filter(Boolean);
  return bundleSkus.find((bundleSku, index) => bundleSkus.indexOf(bundleSku) !== index);
};

export default function BundleRecipesPage() {
  const [drafts, setDrafts] = useState<RecipeDraft[]>([]);
  const [savedDrafts, setSavedDrafts] = useState<RecipeDraft[]>([]);
  const [version, setVersion] = useState(0);
  const [notYetCreated, setNotYetCreated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Saving replaces every recipe, so the form stays closed unless the stored list was read in full.
  const [editable, setEditable] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const clientRef = useRef<SiteConfigClient | null>(null);

  const getClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = new SiteConfigClient(getAdminApiUrl(AdminApiDomain.SITE_CONFIG));
    }
    return clientRef.current;
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const entry = await getClient().getSiteConfig(BUNDLE_RECIPES_CONFIG_KEY);
        const storedError = validateBundleRecipesConfig(entry.config);
        if (storedError) {
          setError(`The stored recipes cannot be edited here: ${storedError}`);
          return;
        }
        const loaded = toDrafts(entry.config as BundleRecipesConfig);
        setDrafts(loaded);
        setSavedDrafts(loaded);
        setVersion(entry.version);
        setEditable(true);
      } catch (loadError) {
        const status = (loadError as { response?: { status?: number } } | null)?.response?.status;
        if (status === 404) {
          setNotYetCreated(true);
          setEditable(true);
        } else {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load recipes');
        }
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [getClient]);

  const updateRecipe = (recipeIndex: number, update: (recipe: RecipeDraft) => RecipeDraft) => {
    setDrafts((current) => current.map((recipe, index) => (index === recipeIndex ? update(recipe) : recipe)));
  };

  const updateComponent = (recipeIndex: number, componentIndex: number, change: Partial<ComponentDraft>) => {
    updateRecipe(recipeIndex, (recipe) => ({
      ...recipe,
      components: recipe.components.map((component, index) =>
        index === componentIndex ? { ...component, ...change } : component,
      ),
    }));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    const duplicateBundleSku = findDuplicateBundleSku(drafts);
    if (duplicateBundleSku) {
      setError(`Bundle ${duplicateBundleSku} is listed more than once`);
      return;
    }

    const config = toConfig(drafts);
    const validationError = validateBundleRecipesConfig(config);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!window.confirm('Save these recipes? The list on this page replaces all stored recipes.')) {
      return;
    }

    setSaving(true);
    try {
      const entry = await getClient().setSiteConfig(BUNDLE_RECIPES_CONFIG_KEY, config);
      const saved = toDrafts(config);
      setDrafts(saved);
      setSavedDrafts(saved);
      setVersion(entry.version);
      setNotYetCreated(false);
      setSuccess('Bundle recipes saved');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save recipes');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(drafts) !== JSON.stringify(savedDrafts);

  if (loading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 px-3">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="d-flex align-items-center gap-2">
          <Layers size={24} />
          <h5 className="mb-0">Bundle Recipes</h5>
          {notYetCreated && <Badge bg="secondary">Not Set</Badge>}
          {hasChanges && (
            <Badge bg="warning" text="dark">
              Unsaved Changes
            </Badge>
          )}
        </div>
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || !hasChanges}>
          {saving ? <Spinner animation="border" size="sm" /> : <Save size={14} className="me-1" />}
          Save
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <AlertTriangle size={14} className="me-1" />
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
          <CheckCircle size={14} className="me-1" />
          {success}
        </Alert>
      )}

      {editable && (
        <Card>
          <Card.Header className="d-flex align-items-center justify-content-between py-2">
            <span className="small fw-medium">
              Site Config: <code>{BUNDLE_RECIPES_CONFIG_KEY}</code>
            </span>
            {version > 0 && (
              <span className="d-flex align-items-center gap-1 small text-muted">
                <Clock size={12} />
                {new Date(version < 1e12 ? version * 1000 : version).toLocaleString()}
              </span>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            <Table size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: '12rem' }}>Bundle SKU</th>
                  <th>Components (SKU and quantity in one bundle)</th>
                  <th style={{ width: '3rem' }} />
                </tr>
              </thead>
              <tbody>
                {drafts.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-muted py-3">
                      No recipes yet
                    </td>
                  </tr>
                )}
                {drafts.map((recipe, recipeIndex) => {
                  const recipeNumber = recipeIndex + 1;
                  return (
                    <tr key={recipeIndex}>
                      <td className="align-top">
                        <Form.Control
                          size="sm"
                          aria-label={`Recipe ${recipeNumber} bundle SKU`}
                          value={recipe.bundleSku}
                          onChange={(event) =>
                            updateRecipe(recipeIndex, (current) => ({ ...current, bundleSku: event.target.value }))
                          }
                          disabled={saving}
                        />
                      </td>
                      <td>
                        {recipe.components.map((component, componentIndex) => {
                          const componentLabel = `Recipe ${recipeNumber} component ${componentIndex + 1}`;
                          return (
                            <div key={componentIndex} className="d-flex align-items-center gap-2 mb-1">
                              <Form.Control
                                size="sm"
                                aria-label={`${componentLabel} SKU`}
                                value={component.sku}
                                onChange={(event) =>
                                  updateComponent(recipeIndex, componentIndex, { sku: event.target.value })
                                }
                                disabled={saving}
                                style={{ minWidth: '9rem', maxWidth: '16rem' }}
                              />
                              <span className="text-muted">×</span>
                              <Form.Control
                                size="sm"
                                type="number"
                                min={1}
                                step={1}
                                aria-label={`${componentLabel} quantity`}
                                value={component.quantity}
                                onChange={(event) =>
                                  updateComponent(recipeIndex, componentIndex, { quantity: event.target.value })
                                }
                                disabled={saving}
                                style={{ maxWidth: '6rem' }}
                              />
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-danger"
                                aria-label={`Remove ${componentLabel.toLowerCase()}`}
                                onClick={() =>
                                  updateRecipe(recipeIndex, (current) => ({
                                    ...current,
                                    components: current.components.filter((_, index) => index !== componentIndex),
                                  }))
                                }
                                disabled={saving}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          );
                        })}
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0"
                          aria-label={`Add component to recipe ${recipeNumber}`}
                          onClick={() =>
                            updateRecipe(recipeIndex, (current) => ({
                              ...current,
                              components: [...current.components, EMPTY_COMPONENT],
                            }))
                          }
                          disabled={saving}
                        >
                          <Plus size={14} className="me-1" />
                          Add component
                        </Button>
                      </td>
                      <td className="align-top text-end">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-danger"
                          aria-label={`Remove recipe ${recipeNumber}`}
                          onClick={() => setDrafts((current) => current.filter((_, index) => index !== recipeIndex))}
                          disabled={saving}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
          <Card.Footer className="py-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => setDrafts((current) => [...current, { bundleSku: '', components: [EMPTY_COMPONENT] }])}
              disabled={saving}
            >
              <Plus size={14} className="me-1" />
              Add recipe
            </Button>
          </Card.Footer>
        </Card>
      )}
    </Container>
  );
}
